import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react"

export interface MeData {
  current_user: {
    id: number
    username: string
    avatar_url: string | null
  } | null
  unread_notifications_count: number
  staff: boolean
  managed_coffee_shop_slug: string | null
  flash: Record<string, string>
  bookmarks: Record<string, number>
  collections: Array<{ uuid: string; name: string }>
}

interface CurrentUserContextValue {
  data: MeData | null
  isLoading: boolean
}

const CurrentUserContext = createContext<CurrentUserContextValue>({
  data: null,
  isLoading: true,
})

const ME_FETCH_INIT = {
  credentials: "same-origin" as const,
  // Chrome: do not compete with the LCP image (fetchpriority=high).
  priority: "low" as const,
}

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<MeData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    let started = false

    const fetchMe = () => {
      if (cancelled || started) return
      started = true

      fetch("/me", ME_FETCH_INIT)
        .then((res) => res.json())
        .then((json: MeData & { csrf_token?: string }) => {
          if (cancelled) return
          if (json.csrf_token) {
            document
              .querySelector('meta[name="csrf-token"]')
              ?.setAttribute("content", json.csrf_token)
          }
          setData(json)
          setIsLoading(false)
        })
        .catch(() => {
          if (!cancelled) setIsLoading(false)
        })
    }

    // Uncacheable personalization. Wait for window load so /me is not
    // on the LCP image's Slow-4G pipe. Keep isLoading until then so
    // the header does not flash Sign in → avatar. Pointer/key starts
    // it earlier for real users; lab does not fire those.
    if (document.readyState === "complete") {
      fetchMe()
    } else {
      window.addEventListener("load", fetchMe)
    }
    window.addEventListener("pointerdown", fetchMe, { passive: true })
    window.addEventListener("keydown", fetchMe)

    return () => {
      cancelled = true
      window.removeEventListener("load", fetchMe)
      window.removeEventListener("pointerdown", fetchMe)
      window.removeEventListener("keydown", fetchMe)
    }
  }, [])

  return (
    <CurrentUserContext.Provider value={{ data, isLoading }}>
      {children}
    </CurrentUserContext.Provider>
  )
}

export function useCurrentUser(): CurrentUserContextValue {
  return useContext(CurrentUserContext)
}
